/**
 * BetaFeedbackButton — the Residence's quiet suggestion box.
 *
 * A hairline chip pinned to the bottom-right of every room. Opens a calm
 * ivory sheet where the resident can leave a note for ARA's team.
 */
import { useEffect, useState } from 'react';
import { MessageSquare, Send, Bug, Lightbulb, Heart, Settings } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const CATEGORIES = [
  { id: 'bug', label: 'Bug report', icon: Bug },
  { id: 'usability', label: 'Usability', icon: Settings },
  { id: 'feature_request', label: 'Idea', icon: Lightbulb },
  { id: 'testimonial', label: 'Testimonial', icon: Heart },
];

const SESSION_KEY = 'ara-session-started-at';
const ROOMS_KEY = 'ara-rooms-visited';
const TEN_MINUTES = 10 * 60 * 1000;

/* Feedback only appears after the resident has actually lived in ARA a
   little: ~10 minutes of session time OR 3 rooms visited. */
function useFeedbackReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let start = Number(localStorage.getItem(SESSION_KEY));
    if (!start) {
      start = Date.now();
      localStorage.setItem(SESSION_KEY, String(start));
    }
    let rooms = [];
    try { rooms = JSON.parse(localStorage.getItem(ROOMS_KEY) || '[]'); } catch { rooms = []; }
    const path = window.location.pathname;
    if (!rooms.includes(path)) {
      rooms.push(path);
      localStorage.setItem(ROOMS_KEY, JSON.stringify(rooms));
    }
    const check = () => setReady(rooms.length >= 3 || Date.now() - start >= TEN_MINUTES);
    check();
    const iv = setInterval(check, 30000);
    return () => clearInterval(iv);
  }, []);
  return ready;
}

export default function BetaFeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('usability');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const ready = useFeedbackReady();

  const handleSubmit = async () => {
    if (!feedbackText.trim() || feedbackText.length < 10) {
      toast.error('Please add a little more so ARA can act on it.');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.beta.submitFeedback({
        feedback_text: feedbackText.trim(),
        feedback_category: category,
      });
      if (response.ok) {
        toast.success('Thank you — noted.');
        setIsOpen(false);
        setFeedbackText('');
        setCategory('usability');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Could not send just now.');
      }
    } catch {
      toast.error('Could not send just now.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) return null;

  return (
    <>
      {/* Hairline chip — Residence tokens only */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full transition-ara hover:opacity-80"
        style={{
          background: 'var(--ara-surface)',
          border: '1px solid var(--ara-line)',
          color: 'var(--ara-ink)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '12px',
          fontWeight: 500,
          letterSpacing: '0.02em',
        }}
        data-testid="beta-feedback-btn"
      >
        <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
        <span>Feedback</span>
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="sm:max-w-md"
          style={{ background: 'var(--ara-surface)', border: '1px solid var(--ara-line)' }}
        >
          <DialogHeader>
            <DialogTitle asChild>
              <h2
                className="text-[22px] leading-tight tracking-[-0.01em]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
              >
                Leave a note for ARA
              </h2>
            </DialogTitle>
            <DialogDescription asChild>
              <p
                className="mt-1 text-[13.5px] leading-[1.5]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
              >
                What did you notice? Small remarks shape the Residence.
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-5">
            {/* Category chips */}
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Feedback category">
              {CATEGORIES.map((cat) => {
                const active = category === cat.id;
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setCategory(cat.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-ara"
                    style={{
                      background: active ? 'var(--ara-ink)' : 'transparent',
                      color: active ? 'var(--ara-surface)' : 'var(--ara-ink-muted)',
                      border: `1px solid ${active ? 'var(--ara-ink)' : 'var(--ara-line)'}`,
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                    data-testid={`category-${cat.id}`}
                  >
                    <Icon className="w-3 h-3" strokeWidth={1.5} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            <Textarea
              placeholder="Tell us what would help…"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="min-h-[120px] resize-none rounded-lg"
              style={{
                background: 'transparent',
                border: '1px solid var(--ara-line)',
                color: 'var(--ara-ink)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '14px',
              }}
              data-testid="feedback-text-input"
            />

            <p
              className="caption-ara"
              style={{ color: 'var(--ara-ink-soft)' }}
            >
              {feedbackText.length < 10
                ? `${10 - feedbackText.length} more characters`
                : `${feedbackText.length} characters`}
            </p>
          </div>

          <div className="flex items-center justify-end gap-6 pt-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="link-ara"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || feedbackText.length < 10}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 transition-ara disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-80"
              style={{
                background: 'var(--ara-ink)',
                color: 'var(--ara-surface)',
                border: '1px solid var(--ara-ink)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
              data-testid="submit-feedback-btn"
            >
              {submitting ? (
                <>
                  <span
                    className="w-3 h-3 rounded-full border animate-spin"
                    style={{ borderColor: 'rgba(250, 249, 246, 0.3)', borderTopColor: 'var(--ara-surface)' }}
                  />
                  Sending
                </>
              ) : (
                <>
                  <Send className="w-3 h-3" strokeWidth={1.5} />
                  Send
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
